import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, Terminal } from "lucide-react";
import { ASSETS, LINKS } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { WalletButton } from "@/components/Wallet";

// Clean X (Twitter) mark
export function XLogo({ size = 15, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const NAV_ITEMS = [
  { to: "/", key: "nav.home", testid: "nav-home" },
  { to: "/terminal", key: "nav.terminal", testid: "nav-terminal" },
  { to: "/lore", key: "nav.lore", testid: "nav-lore" },
  { to: "/agent", key: "nav.agent", testid: "nav-agent" },
  { to: "/economy", key: "nav.economy", testid: "nav-economy" },
  { to: "/roadmap", key: "nav.roadmap", testid: "nav-roadmap" },
  { to: "/token", key: "nav.token", testid: "nav-token" },
];

export function LangToggle({ className = "" }) {
  const { lang, setLang } = useLanguage();
  return (
    <div
      className={`flex items-center border border-mizu-cyan/25 font-mono text-[10px] tracking-[0.15em] ${className}`}
      data-testid="lang-toggle"
    >
      <button
        data-testid="lang-en"
        onClick={() => setLang("en")}
        className={`px-2 py-1 transition-colors ${lang === "en" ? "bg-mizu-cyan/15 text-mizu-cyan" : "text-mizu-faint hover:text-mizu-dim"}`}
      >
        EN
      </button>
      <span className="h-3 w-px bg-mizu-cyan/25" />
      <button
        data-testid="lang-jp"
        onClick={() => setLang("jp")}
        className={`px-2 py-1 font-jp transition-colors ${lang === "jp" ? "bg-mizu-cyan/15 text-mizu-cyan" : "text-mizu-faint hover:text-mizu-dim"}`}
      >
        日本語
      </button>
    </div>
  );
}

export default function Navbar() {
  const { t, lang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`sticky top-0 z-50 border-b border-mizu-cyan/15 bg-[#020611]/80 backdrop-blur-md transition-all duration-300 ${scrolled ? "shadow-[0_4px_30px_rgba(0,240,255,0.06)]" : ""}`}
      >
        <div
          className={`mx-auto flex max-w-7xl items-center justify-between px-4 transition-all duration-300 sm:px-6 ${scrolled ? "h-12" : "h-16"}`}
        >
          {/* Brand */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
            <img
              src={ASSETS.logo}
              alt="MIZUKARA logo"
              className={`rounded-full transition-all duration-300 ${scrolled ? "h-7 w-7" : "h-9 w-9"}`}
            />
            <div className="leading-none">
              <div className={`font-display font-bold tracking-[0.18em] text-white transition-all duration-300 ${scrolled ? "text-sm" : "text-base"}`}>
                MIZUKARA
              </div>
              <div className="mt-0.5 font-jp text-[9px] tracking-[0.4em] text-mizu-cyan/80">
                ミズカラ
              </div>
            </div>
          </Link>

          {/* Center nav */}
          <nav className="hidden items-center gap-5 lg:flex xl:gap-7" data-testid="nav-links">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] tracking-[0.2em] transition-colors duration-200 ${
                    isActive ? "text-mizu-cyan mizu-glow" : "text-mizu-dim hover:text-white"
                  } ${lang === "jp" ? "font-jp" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    {t(item.key)}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-1.5 left-0 h-px w-full bg-mizu-cyan shadow-[0_0_8px_rgba(0,240,255,0.8)]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3">
            <a
              href={LINKS.x}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-x-link"
              aria-label="X"
              className="hidden p-1.5 text-mizu-dim transition-colors hover:text-mizu-cyan sm:block"
            >
              <XLogo />
            </a>
            <LangToggle className="hidden sm:flex" />
            <div className="hidden md:block">
              <WalletButton testid="nav-connect-wallet" />
            </div>
            <button
              data-testid="nav-menu-button"
              onClick={() => setMenuOpen(true)}
              className="p-1.5 text-mizu-dim transition-colors hover:text-mizu-cyan lg:hidden"
              aria-label={t("nav.menu")}
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full-screen terminal menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[80] flex flex-col bg-[#020611]/97 backdrop-blur-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center justify-between border-b border-mizu-cyan/15 px-5 py-4">
              <div className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-mizu-cyan">
                <Terminal size={14} />
                MIZUKARA // MENU
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="font-mono text-xs tracking-[0.25em] text-mizu-dim hover:text-white"
              >
                [ {t("nav.close")} ]
              </button>
            </div>

            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05 }}
                >
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${item.testid}`}
                    className={({ isActive }) =>
                      `group flex items-baseline gap-4 border-b border-mizu-cyan/10 py-4 ${
                        isActive ? "text-mizu-cyan" : "text-mizu-silver"
                      }`
                    }
                  >
                    <span className="font-mono text-[10px] text-mizu-faint">
                      0{i + 1}
                    </span>
                    <span
                      className={`font-display text-xl tracking-[0.2em] ${lang === "jp" ? "font-jp" : ""}`}
                    >
                      {t(item.key)}
                    </span>
                    <span className="ml-auto font-mono text-[10px] text-mizu-faint opacity-0 transition-opacity group-hover:opacity-100">
                      &gt;_
                    </span>
                  </NavLink>
                </motion.div>
              ))}
            </nav>

            <div className="flex items-center justify-between border-t border-mizu-cyan/15 px-8 py-5">
              <a
                href={LINKS.x}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-x-link"
                className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-mizu-dim"
              >
                <XLogo size={14} /> X
              </a>
              <LangToggle />
              <WalletButton compact testid="mobile-connect-wallet" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
